import { useCallback } from 'react'
import { deleteEngineSchedule, readEngineSchedules } from '../api/engineSchedulesApi'
import { useScheduleActions } from './useScheduleActions'
import { resolvePersistedEngineScheduleId } from '../utils/resolvePersistedEngineScheduleId'
import { setSchedulingEngineDraft } from 'routes/Studies/routes/Study/routes/Instruments/routes/Instrument/modules/Instrument'

const resolveActiveAfterDelete = (removedScheduleId, activeScheduleId, schedules) => {
  if (!schedules.length) return null
  if (activeScheduleId && activeScheduleId !== removedScheduleId) {
    const stillActive = schedules.some(schedule => schedule.id === activeScheduleId)
    if (stillActive) return activeScheduleId
  }
  return schedules[0]?.id || null
}

export const useScheduleDelete = ({
  studyID,
  instrument,
  dispatch,
  studyVersionID,
  hasStudyPromotion,
}) => {
  const { schedules, activeScheduleId, deleteSchedule, hydrateSchedules, setSaving } = useScheduleActions()

  const syncDraftFromInstrument = useCallback(
    (nextInstrument, nextActiveId) => {
      const { schedules: savedSchedules } = readEngineSchedules(nextInstrument)
      if (!savedSchedules.length) return
      const activeId = resolveActiveAfterDelete(null, nextActiveId, savedSchedules)
      hydrateSchedules(savedSchedules, activeId)
      dispatch(
        setSchedulingEngineDraft({
          instrumentId: nextInstrument?.id,
          schedules: savedSchedules,
          activeScheduleId: activeId,
        }),
      )
    },
    [dispatch, hydrateSchedules],
  )

  const removeSchedule = useCallback(
    async scheduleId => {
      if (schedules.length <= 1) return false
      const target = schedules.find(schedule => schedule.id === scheduleId)
      if (!target) return false

      const persistedScheduleId = resolvePersistedEngineScheduleId(target, instrument)
      if (!studyID || !instrument?.id || !persistedScheduleId) {
        deleteSchedule(scheduleId)
        const remaining = schedules.filter(schedule => schedule.id !== scheduleId)
        const activeId = resolveActiveAfterDelete(scheduleId, activeScheduleId, remaining)
        dispatch(
          setSchedulingEngineDraft({
            instrumentId: instrument?.id,
            schedules: remaining,
            activeScheduleId: activeId,
          }),
        )
        return true
      }

      setSaving(true)
      try {
        const nextInstrument = await dispatch(
          deleteEngineSchedule({
            studyID,
            instrumentID: instrument.id,
            scheduleId: persistedScheduleId,
            studyVersionID,
            hasStudyPromotion,
          }),
        )
        const nextActiveId = resolveActiveAfterDelete(scheduleId, activeScheduleId, readEngineSchedules(nextInstrument).schedules)
        syncDraftFromInstrument(nextInstrument, nextActiveId)
        return true
      } catch {
        return false
      } finally {
        setSaving(false)
      }
    },
    [
      activeScheduleId,
      deleteSchedule,
      dispatch,
      hasStudyPromotion,
      instrument,
      schedules,
      setSaving,
      studyID,
      studyVersionID,
      syncDraftFromInstrument,
    ],
  )

  return { removeSchedule }
}
